import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import { selectIsAuthenticated } from '@paradigm/store/auth';
import { UnifiedRfqTradeTapeProvider } from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';

import {
  FEATURE_BLOCK_TERMINAL,
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

import BlockTerminalTab from '#/components/Terminal/BlockTerminalTab';
import {
  SIDE_PANEL_CHAT_MAX_WIDTH,
  SIDE_PANEL_CHAT_MIN_WIDTH,
} from '#/components/Terminal/layout-constants';
import { LocalTabFiltersProvider } from '#/components/UnifiedRfqs/UnifiedRfqs';

/**
 * Right-rail mount of the Block Terminal. Renders only when the user has
 * pinned the terminal as a side panel and the drawer is open. Both the
 * bottom-panel tabs and the side panel are visible simultaneously, similar
 * to the Paradex layout.
 *
 * The panel's outer width = sessions sidebar width + chat-pane width.
 * The chat-pane width is resized by dragging the panel's left edge; the
 * sessions sidebar width is resized via the middle divider inside the
 * terminal (see `BlockTerminalTab`). Both widths persist per-user.
 */
export default function BlockTerminalSidePanel() {
  const enabled = useIsFeatureFlagEnabled(FEATURE_BLOCK_TERMINAL);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startWidth: drawerState.sidePanelChatWidth,
        currentWidth: drawerState.sidePanelChatWidth,
      };
      setIsDragging(true);
    },
    [drawerState.sidePanelChatWidth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      // Dragging left grows the chat pane (panel extends to the left).
      const next = clamp(
        r.startWidth - (e.clientX - r.startX),
        SIDE_PANEL_CHAT_MIN_WIDTH,
        SIDE_PANEL_CHAT_MAX_WIDTH,
      );
      r.currentWidth = next;
      drawerActions.setSidePanelChatWidth(next);
    },
    [drawerActions],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released — ignore
      }
      // Persist once at end of drag — pointer-move only wrote to
      // in-memory state to avoid 60-writes-per-second to localStorage.
      drawerActions.commitSidePanelChatWidth(r.currentWidth);
      dragRef.current = null;
      setIsDragging(false);
    },
    [drawerActions],
  );

  if (!isAuthenticated) return null;
  if (!enabled) return null;
  if (drawerState.pinMode !== 'side-panel') return null;
  if (!drawerState.isOpen) return null;

  const sidebarContribution = drawerState.sidebarCollapsed
    ? 0
    : drawerState.sidebarWidth;
  const totalWidth = drawerState.sidePanelChatWidth + sidebarContribution;

  return (
    <Panel $width={totalWidth} $dragging={isDragging}>
      <LeftEdgeHandle
        $dragging={isDragging}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label="Resize terminal side panel"
        role="separator"
        aria-orientation="vertical"
      />
      <LocalTabFiltersProvider storageKey="trade-tape-rfq-tab-filters">
        <UnifiedRfqTradeTapeProvider>
          <BlockTerminalTab />
        </UnifiedRfqTradeTapeProvider>
      </LocalTabFiltersProvider>
    </Panel>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const Panel = styled.aside<{
  readonly $width: number;
  readonly $dragging: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => $width}px;
  max-width: 100vw;
  background: var(--bg-color-container, #0e131a);
  border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  display: flex;
  flex-direction: column;
  z-index: 900;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.35);
  /* Disable the width animation during a drag so the panel tracks the
     cursor 1:1 instead of lagging behind it. */
  transition: ${({ $dragging }) => ($dragging ? 'none' : 'width 0.18s ease')};
`;

const LeftEdgeHandle = styled.div<{ readonly $dragging: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 6px;
  margin-left: -3px;
  cursor: ew-resize;
  touch-action: none;
  z-index: 1;
  transition: background-color 0.2s ease;
  background-color: ${({ $dragging }) =>
    $dragging ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;
